import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";
import { AlertTriangle, CheckCircle2, Eye, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DocumentStatusBadge } from "./DocumentStatusBadge";

interface RequiredDocumentRowProps {
  docId: number;
  canDeleteDocument: boolean;
  documents: any[];
  canUploadDocuments: boolean;
  talentProfileData: any;
  onUpload: (docId: number, docName: string) => void;
  onView: (doc: any) => void;
  onEdit: (doc: any) => void;
  onDeleteUploaded: (docId: number) => void;
  onRemoveRequirement: (id: number, type: "document" | "list") => void;
  canEditFile: (doc: any) => boolean;
}

export function RequiredDocumentRow({
  docId,
  canDeleteDocument,
  documents,
  canUploadDocuments,
  talentProfileData,
  onUpload,
  onView,
  onEdit,
  onDeleteUploaded,
  onRemoveRequirement,
  canEditFile
}: RequiredDocumentRowProps) {
  const { profile: authProfile } = useAuth();
  const { t } = useTranslation();
  const { data: documentTypes = [] } = useDocumentTypes();
  const docType = documentTypes.find((dt) => dt.id === docId);
  const uploadedDoc = documents.find((d) => d.document_type_id === docId);
  const isMissingDocumentType = !docType;

  return (
    <div className="flex items-center justify-between p-3 bg-white border rounded-md">
      <div className="flex items-center gap-3">
        {isMissingDocumentType ? (
          <>
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span className="text-red-600 font-medium text-sm">
              {authProfile?.isSuperUser
                ? `Gelöschtes Dokument (ID: ${docId}) - Bitte Liste aktualisieren`
                : "Unbekanntes Dokument"}
            </span>
          </>
        ) : (
          <>
            {uploadedDoc && <CheckCircle2 className="h-5 w-5 text-green-500" />}
            <span
              className={`font-medium text-sm ${uploadedDoc ? "text-gray-900" : "text-gray-600"}`}
            >
              {docType.name}
            </span>
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        {uploadedDoc ? (
          <>
            <DocumentStatusBadge status={uploadedDoc.document_status} />

            <div className="flex items-center gap-1 border-l pl-2 ml-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={t("common:actions.view", "Ansehen")}
                onClick={(e) => {
                  e.stopPropagation();
                  onView(uploadedDoc);
                }}
              >
                <Eye className="h-4 w-4" />
              </Button>
              {canEditFile(uploadedDoc) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title={t("common:actions.edit", "Bearbeiten")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(uploadedDoc);
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              )}
              {canEditFile(uploadedDoc) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500 hover:text-red-600"
                  title={t("common:actions.delete", "Löschen")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteUploaded(uploadedDoc.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <DocumentStatusBadge status="missing" />
            {(canUploadDocuments ||
              authProfile?.id === talentProfileData?.talent_id) &&
              !isMissingDocumentType && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onUpload(docId, docType?.name || "")}
                >
                  Hochladen
                </Button>
              )}
          </>
        )}

        {authProfile?.isSuperUser && canDeleteDocument && !uploadedDoc && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveRequirement(docId, "document");
            }}
            title="Anforderung löschen"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
